import { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query'
import { AppError, AuthServiceError, ProblemDetails } from '@/types/api.def.ts'
import { Button, notification } from 'antd'
import { CopyButton } from '@/components/CopyButton.tsx'

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export const isFetchBaseQueryError = (
  error: unknown,
): error is FetchBaseQueryError => {
  return typeof error === 'object' && error != null && 'status' in error
}

export const isAppError = (obj: unknown): obj is AppError => {
  return typeof obj === 'object' && obj != null && 'errorCode' in obj && 'message' in obj && 'details' in obj
}

export const isProblemDetails = (obj: unknown): obj is ProblemDetails => {
  return typeof obj === 'object' && obj != null
    && 'errors' in obj && 'title' in obj && 'extensions' in obj
}

export const isAuthServiceError = (obj: unknown): obj is AuthServiceError => {
  return typeof obj === 'object' && obj != null
    && 'timestamp' in obj && 'message' in obj && 'valid' in obj && 'statusCode' in obj
}

export const isFetchBaseQueryAppError = (
  error: unknown,
): error is { status: number, data: AppError } => {
  return isFetchBaseQueryError(error) && isAppError(error.data)
}

export const handleApiError = (error: unknown, message: string = '') => {
  if (isFetchBaseQueryError(error)) {
    // backend controller validation error
    if (isProblemDetails(error.data)) {
      notification.error({
        title: message,
        description: error.data.detail,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      })
    } else if (isAuthServiceError(error.data)) {
      notification.error({
        title: message,
        description: error.data.message,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      })
    } else if (isAppError(error.data)) {
      notification.error({
        title: message,
        description: error.data.message,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      })
    }
  }
}

export const getAppErrorData = <T = unknown, >(error: unknown, message = 'Failed') => {
  if (isFetchBaseQueryError(error)) {
    if (isProblemDetails(error.data)) {
      notification.error({
        title: message,
        description: error.data.detail,
        actions: <Button onClick={() => navigator.clipboard.writeText(JSON.stringify(error.data, null, 2))}>Copy
          Error</Button>,
      })
      return null
    } else if (isAppError(error.data)) {
      return error.data as AppError<T>
    }
  }
}

export const handleSessionExpiredError = (result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>) => {
  if ((result.error && result.error.status === 302) || (result.error && result.error.status === 401)) {
    notification.error({
      key: 'session-expired',
      title: 'Session Expired',
      description: 'Your session has expired. Please login again. Please save your unsaved work before refreshing the page.',
      duration: undefined,
      actions:
        <Button
          danger
          onClick={() => {
            window.location.reload()
          }}
        >
          Refresh
        </Button>,
    })
  }
}